import React from 'react';
import {createTheme, assignProps, createView, view} from '@sail/engine';
import type {IconAsset, SvgType} from '@sail/icons/types';
import {tokens as $} from '@sail/tokens';
import {CheckboxConfig, NativeCheckboxConfig} from '../../components/Checkbox';
import {IconConfig} from '../../components/Icon';
import type {View} from '../../view';

// Replaces the default checkmark when the visual refresh Checkbox is checked.
const VisualRefreshCheckAsset = createView(
  (props: View.IntrinsicElement<'svg'>) => {
    return (
      <view.svg
        {...props}
        css={{
          pointerEvents: 'none',
        }}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M10.2137 5.48689C10.2814 5.41224 10.3637 5.35262 10.4555 5.31184C10.5472 5.27106 10.6463 5.25 10.7465 5.25C10.8468 5.25 10.9459 5.27106 11.0376 5.31184C11.1293 5.35262 11.2117 5.41224 11.2794 5.48689C11.4213 5.64292 11.5 5.84709 11.5 6.05898C11.5 6.27087 11.4213 6.47504 11.2794 6.63107L7.6702 10.5131C7.6025 10.5878 7.52017 10.6474 7.42845 10.6882C7.33672 10.7289 7.2376 10.75 7.13738 10.75C7.03716 10.75 6.93804 10.7289 6.84632 10.6882C6.75459 10.6474 6.67226 10.5878 6.60456 10.5131L4.72065 8.49036C4.57874 8.33433 4.5 8.13016 4.5 7.91827C4.5 7.70638 4.57874 7.50221 4.72065 7.34618C4.78834 7.27153 4.87067 7.21192 4.9624 7.17113C5.05413 7.13035 5.15325 7.10929 5.25346 7.10929C5.35368 7.10929 5.4528 7.13035 5.54453 7.17113C5.63626 7.21192 5.71858 7.27153 5.78628 7.34618L7.1366 8.79684L10.2137 5.48689Z" />
      </view.svg>
    );
  },
);

const visualRefreshCheckIcon: IconAsset = {
  category: 'icon',
  colorable: true,
  name: 'visual-refresh-check',
  svg: VisualRefreshCheckAsset as SvgType,
};

// Replaces the default indeterminate mark.
const VisualRefreshIndeterminateAsset = createView(
  (props: View.IntrinsicElement<'svg'>) => (
    <view.svg
      {...props}
      css={{pointerEvents: 'none'}}
      width="8"
      height="2"
      viewBox="0 0 8 2"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 1C0 0.447715 0.358172 0 0.8 0H7.2C7.64183 0 8 0.447715 8 1C8 1.55228 7.64183 2 7.2 2H0.8C0.358172 2 0 1.55228 0 1Z" />
    </view.svg>
  ),
);

const visualRefreshIndeterminateIcon: IconAsset = {
  category: 'icon',
  colorable: true,
  name: 'visual-refresh-indeterminate',
  svg: VisualRefreshIndeterminateAsset as SvgType,
};

const visualRefreshNativeCheckboxOverride = NativeCheckboxConfig.customize({
  css: {
    height: 'medium',
    width: 'medium',
    keyline: $['color.hue.slate.100'],
    boxShadow: '0 0 0 0 transparent',

    ':not([disabled])': {
      boxShadow: '0 0 0 0 transparent',
    },

    ':disabled': {
      backgroundColor: $['color.hue.slate.25'],
    },

    ':disabled:checked': {
      backgroundColor: $['color.hue.blurple.400'],
      keyline: $['color.hue.blurple.400'],
    },

    ':checked': {
      backgroundColor: $['color.hue.blurple.600'],
      keyline: $['color.hue.blurple.600'],
    },

    '[aria-invalid="true"]': {
      keyline: $['color.hue.red.600'],
    },

    '[aria-invalid="true"]:checked': {
      keyline: $['color.hue.red.600'],
    },
  },
  subviews: {
    checked: {
      css: {
        height: 'space.200',
        width: 'space.200',
      },
      uses: [assignProps({icon: visualRefreshCheckIcon})],
    },
    indeterminate: {
      css: {
        height: 'space.25',
        width: 'space.100',
      },
      uses: [assignProps({icon: visualRefreshIndeterminateIcon})],
    },
  },
  forward: {indeterminate: true},
  variants: {
    indeterminate: {
      true: {
        css: {
          backgroundColor: $['color.hue.blurple.600'],
          keyline: $['color.hue.blurple.600'],
          ':disabled': {
            backgroundColor: $['color.hue.blurple.400'],
            keyline: $['color.hue.blurple.400'],
          },
        },
      },
    },
  },
});

const visualRefreshCheckboxOverride = CheckboxConfig.customize({
  css: {
    gapY: 'xxsmall',
  },
  subviews: {
    label: {
      css: {
        paddingLeft: 'medium',
      },
      uses: [assignProps({size: 'medium', emphasis: 'normal'})],
    },
    description: {
      css: {
        font: 'label.medium',
        paddingLeft: 'medium',
      },
    },
    error: {
      css: {
        color: $['color.hue.red.600'],
        font: 'label.medium',
        paddingLeft: 'medium',
      },
      uses: [IconConfig.customize({css: {display: 'none'}})],
    },
  },
});

export const visualRefreshCheckboxTheme = createTheme(
  {
    name: 'Visual Refresh Checkbox',
    description: 'Visual refresh styling for Checkbox.',
    category: 'Visual Refresh',
  },
  [visualRefreshNativeCheckboxOverride, visualRefreshCheckboxOverride],
);
